<template>
  <!-- Set-new-password modal, shown when the user arrives via a password-reset
       email link (Supabase PASSWORD_RECOVERY). The recovery link has already
       signed the user in; this collects the replacement password. Dismissible:
       closing keeps the session, the user just keeps their old password. -->
  <AppModal
    v-model="show"
    size="sm"
    :mobile-sheet="false"
    :aria-label="t('auth.recovery.title')"
    initial-focus="first-input"
    @close="dismiss"
  >
    <template #header>
      <div class="flex items-center justify-between px-6 py-4 shrink-0" style="border-bottom: 1px solid var(--border-subtle);">
        <h2 id="password-recovery-title" class="m-0" style="font-size: var(--text-xl); color: var(--text-primary);">{{ t('auth.recovery.title') }}</h2>
        <AppButton icon-only :aria-label="t('common.close')" class="text-2xl" @click="dismiss">×</AppButton>
      </div>
    </template>

    <p class="mb-4" style="font-size: var(--text-sm); color: var(--text-secondary);">{{ t('auth.recovery.description') }}</p>

    <form @submit.prevent="handleSubmit">
      <div class="mb-3">
        <AppInput
          v-model="password"
          type="password"
          name="new-password"
          autocomplete="new-password"
          :placeholder="t('auth.recovery.new_password')"
          :aria-label="t('auth.recovery.new_password')"
          required
          :disabled="isLoading"
          :minlength="6"
        />
      </div>

      <div class="mb-3">
        <AppInput
          v-model="confirmPassword"
          type="password"
          name="confirm-password"
          autocomplete="new-password"
          :placeholder="t('auth.recovery.confirm_password')"
          :aria-label="t('auth.recovery.confirm_password')"
          required
          :disabled="isLoading"
          :minlength="6"
        />
      </div>

      <AppAlert v-if="mismatch" variant="danger" class="mb-3">{{ t('auth.recovery.mismatch') }}</AppAlert>
      <AppAlert v-else-if="error" variant="danger" class="mb-3">{{ error }}</AppAlert>

      <AppButton type="submit" block :loading="isLoading" :disabled="!isFormValid">
        {{ t('auth.recovery.submit') }}
      </AppButton>
    </form>
  </AppModal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "../composables/useToast"
import { authService } from "../services/authService"
import { localizeAuthError } from "../utils/authErrors"
import { AppAlert, AppButton, AppInput, AppModal } from "./ui"

const { t } = useI18n()
const toast = useToast()

const show = ref(false)
const password = ref("")
const confirmPassword = ref("")
const isLoading = ref(false)
const error = ref<string | null>(null)

const mismatch = computed(() => confirmPassword.value.length > 0 && password.value !== confirmPassword.value)
const isFormValid = computed(() => password.value.length >= 6 && password.value === confirmPassword.value)

let unsubscribe: (() => void) | null = null

onMounted(() => {
  unsubscribe = authService.onPasswordRecovery(() => {
    show.value = true
  })
})

onUnmounted(() => {
  unsubscribe?.()
})

function dismiss() {
  show.value = false
  authService.clearPasswordRecovery()
  password.value = ""
  confirmPassword.value = ""
  error.value = null
}

async function handleSubmit() {
  if (!isFormValid.value || isLoading.value) return

  isLoading.value = true
  error.value = null

  try {
    await authService.updatePassword(password.value)
    toast.success(t("auth.recovery.success"))
    dismiss()
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}
</script>
